import { useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { BusinessHoursModal } from "./BusinessHoursModal";

interface OperatingStatus {
  text: string;
  color: string;
}

interface BusinessTimeType {
  cdDayOfWeek: string; // "MONDAY", "TUESDAY", etc.
  startTime: string; // "09:00:00"
  endTime: string; // "18:00:00"
  close: boolean; // 휴무 여부
}

interface CompanyBasicInfoCardProps {
  companyName: string;
  companyAddr: string;
  companySeq: number;
  masterYn?: string;
  mainTelNumber?: string;
  operatingStatus: OperatingStatus;
  businessTimes?: BusinessTimeType[];
  showReservationButton?: boolean;
}

export const CompanyBasicInfoCard = ({
  companyName,
  companyAddr,
  companySeq,
  masterYn,
  mainTelNumber,
  operatingStatus: _operatingStatus,
  businessTimes = [],
  showReservationButton = false,
}: CompanyBasicInfoCardProps) => {
  const { history } = useRouter();
  const [isBusinessHoursModalOpen, setIsBusinessHoursModalOpen] =
    useState(false);

  // 오늘 요일 확인 (0: 일요일, 1: 월요일, ..., 6: 토요일)
  const getTodayDayName = () => {
    const today = new Date();
    const dayIndex = today.getDay();
    const dayNames = [
      "일요일",
      "월요일",
      "화요일",
      "수요일",
      "목요일",
      "금요일",
      "토요일",
    ];
    return dayNames[dayIndex];
  };

  // 오늘 요일의 영업시간 가져오기
  const getTodayBusinessHours = () => {
    if (!businessTimes || businessTimes.length === 0) {
      return null;
    }

    // 오늘 요일을 영어로 변환
    const today = new Date();
    const dayIndex = today.getDay();
    const dayNamesEnglish = [
      "SUNDAY",
      "MONDAY",
      "TUESDAY",
      "WEDNESDAY",
      "THURSDAY",
      "FRIDAY",
      "SATURDAY",
    ];
    const todayDayEnglish = dayNamesEnglish[dayIndex];

    // 오늘 요일에 해당하는 영업시간 찾기
    const todayTime = businessTimes.find(
      (time) => time.cdDayOfWeek === todayDayEnglish,
    );

    if (!todayTime || todayTime.close) {
      return null;
    }

    // 시간 포맷 변환: "09:00:00" -> "09:00"
    const formatTime = (time: string) => {
      if (!time) return "";
      return time.substring(0, 5);
    };

    return `${formatTime(todayTime.startTime)} - ${formatTime(todayTime.endTime)}`;
  };

  const todayBusinessHours = getTodayBusinessHours();

  // 비가맹(마스터/미가입) 매장: 앱내 문의하기 대신 전화만 노출
  const isMasterCompany = (masterYn || "").trim().toUpperCase() === "Y";
  const phone = (mainTelNumber || "").replace(/[^\d+]/g, "");
  const hasPhone = phone.length > 0;

  // 가맹점 — 앱내 문의하기
  const handleAppInquiry = () => {
    history.push(
      `/customer/layout/inform-send-fix?companySeq=${companySeq}&companyName=${companyName}`,
    );
  };

  // 전화 걸기
  const handleCall = () => {
    if (!hasPhone) {
      window.alert("전화번호 정보가 없습니다.");
      return;
    }
    window.location.href = `tel:${phone}`;
  };

  // 정보 수정 및 제안 (가맹·비가맹 공통)
  const handleSuggest = () => {
    history.push(
      `/customer/layout/info-suggest?companySeq=${companySeq}&companyName=${encodeURIComponent(
        companyName,
      )}`,
    );
  };

  return (
    <div className="px-4 pt-4 mb-3">
      <div className="p-4 bg-white rounded-xl shadow-sm">
        {/* 영업 상태 배지 */}
        {/* <div className="flex justify-between items-center mb-4">
          <span
            className={`px-3 py-1 text-xs font-semibold text-white rounded-full ${operatingStatus.color}`}
          >
            {operatingStatus.text}
          </span>
        </div> */}

        {/* 주소 정보 */}
        <div className="flex gap-2 items-start mb-4">
          <svg
            className="flex-shrink-0 mt-0.5 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <p className="flex-1 text-sm leading-relaxed text-gray-700">
            {companyAddr}
          </p>
        </div>

        {/* 영업시간 정보 */}
        {businessTimes && businessTimes.length > 0 && (
          <div
            className="flex gap-2 items-center mb-4 cursor-pointer"
            onClick={() => setIsBusinessHoursModalOpen(true)}
          >
            <svg
              className="flex-shrink-0 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">
                {todayBusinessHours ? (
                  <>
                    오늘 ({getTodayDayName()}) {todayBusinessHours}
                  </>
                ) : (
                  <>오늘 ({getTodayDayName()}) 휴무</>
                )}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">영업시간 보기</p>
            </div>
            <svg
              className="flex-shrink-0 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        )}

        {/* 전화 정보 행 — 번호가 있을 때 노출. 비가맹은 전화가 메인이라 강조 */}
        {hasPhone && (
          <div
            className={`flex gap-2 items-center mb-4 cursor-pointer ${
              isMasterCompany ? "-mx-1 px-1 py-1 rounded-lg bg-yellow/5" : ""
            }`}
            onClick={handleCall}
          >
            <svg
              className="flex-shrink-0 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            <div className="flex-1 text-sm font-medium text-gray-700">
              {mainTelNumber}
            </div>
            <span className="text-xs font-semibold text-yellow">
              {isMasterCompany ? "전화하기" : "전화"}
            </span>
          </div>
        )}

        {/* 액션 버튼 — 비가맹은 문의하기 숨김, 전화하기가 메인 */}
        {isMasterCompany ? (
          hasPhone ? (
            <button
              className="flex justify-center items-center py-3 w-full rounded-lg transition-colors bg-yellow hover:bg-yellow/90"
              onClick={handleCall}
            >
              <span className="text-sm font-semibold text-white">전화하기</span>
            </button>
          ) : null
        ) : showReservationButton ? (
          <div className="flex gap-3">
            <button
              className="flex justify-center items-center flex-1 py-3 rounded-lg transition-colors bg-yellow hover:bg-yellow/90"
              onClick={handleAppInquiry}
            >
              <span className="text-sm font-semibold text-white">문의하기</span>
            </button>
            <button
              className="flex justify-center items-center flex-1 py-3 rounded-lg transition-colors bg-yellow hover:bg-yellow/90"
              onClick={() => {
                history.push(
                  `/customer/layout/reservation-with-subscription?companySeq=${companySeq}`,
                );
              }}
            >
              <span className="text-sm font-semibold text-white">예약하기</span>
            </button>
          </div>
        ) : (
          <button
            className="flex justify-center items-center py-3 w-full rounded-lg transition-colors bg-yellow hover:bg-yellow/90"
            onClick={handleAppInquiry}
          >
            <span className="text-sm font-semibold text-white">문의하기</span>
          </button>
        )}

        {/* 정보 수정 및 제안 — 가맹·비가맹 공통 */}
        <button
          type="button"
          onClick={handleSuggest}
          className="flex gap-1.5 justify-center items-center pt-3 mt-3 w-full border-t border-gray-100"
        >
          <svg
            className="flex-shrink-0 w-3.5 h-3.5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          <span className="text-xs text-gray-400">
            정보가 정확하지 않나요?{" "}
            <span className="text-gray-500 underline">정보 수정 및 제안</span>
          </span>
        </button>
      </div>

      {/* 영업시간 모달 */}
      {businessTimes && businessTimes.length > 0 && (
        <BusinessHoursModal
          isOpen={isBusinessHoursModalOpen}
          onClose={() => setIsBusinessHoursModalOpen(false)}
          businessTimes={businessTimes}
        />
      )}
    </div>
  );
};
